import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { Guid } from 'guid-typescript';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { CsBuildupStateQuery, CsBuildupStateStore, EditableSectionPartComponent } from '../shared';
import { SubsidySection } from './service/subsidy-section.model';
import { SubsidySectionService } from './service/subsidy-section.service';

interface EditableSubsidySection extends SubsidySection {
  readonly id: Guid;
  readonly createdOn: Date;
  subsidyName: string;
  comment: string;
  fundingAmount: number;
  applyOnGross: boolean;
}
@Component({
  selector: 'app-subsidy-section',
  templateUrl: './subsidy-section.component.html',
  styleUrls: ['./subsidy-section.component.scss']
})
export class SubsidySectionComponent extends EditableSectionPartComponent implements OnInit, OnDestroy {
  @Input() projectId: Guid;
  filteredOptions: Observable<string[]>;
  readonly subsidyNames;
  private subsidyName$: BehaviorSubject<string>;
  private loadedSubsidySection: SubsidySection;
  private internalSubsidySection: EditableSubsidySection;
  private internalIsBusy: boolean;

  get isBusy(): boolean {
    return this.internalIsBusy;
  }

  get hasValidSubsidySection(): boolean {
    return this.internalSubsidySection &&
      this.internalSubsidySection.subsidyName &&
      this.internalSubsidySection.fundingAmount > 0;
  }

  get hasSubsidySection(): boolean {
    return !!this.internalSubsidySection;
  }

  get subsidySection(): EditableSubsidySection {
    return this.internalSubsidySection;
  }

  get subsidyName(): string {
    return this?.internalSubsidySection.subsidyName;
  }

  @Input() set subsidyName(value: string) {
    this.internalSubsidySection = { ...this.internalSubsidySection, subsidyName: value};
    this.subsidyName$.next(value);
  }

  constructor(
    private service: SubsidySectionService,
    protected element: ElementRef<Element>,
    protected csBuildupStore: CsBuildupStateStore,
    protected csBuildupStateQuery: CsBuildupStateQuery) {
    super(element, csBuildupStore, csBuildupStateQuery);
    this.subsidyNames = [ 'progres.nrw', 'KfW', 'NLStBV' ];
    this.service.isBusy$.subscribe(isBusy => this.internalIsBusy = isBusy);
    this.subsidyName$ = new BehaviorSubject<string>('');
    this.filteredOptions = this.subsidyName$
      .pipe(
        map(value => this.filterSubsidyName(value))
      );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.service.getSubsidySection(this.projectId)
      .subscribe(data => {
        this.loadedSubsidySection = data;
        this.internalSubsidySection = data ? {...data} : undefined;
      });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  initializeSubsidySection(): void {
    this.internalSubsidySection = {
      id: Guid.create(),
      createdOn: new Date(),
      subsidyName: undefined,
      comment: undefined,
      fundingAmount: undefined,
      applyOnGross: undefined
    };
    this.startEdit();
  }

  deleteSubsidySection(): void {
    if (this.loadedSubsidySection) {
      this.service.deleteSubsidySection(this.projectId)
        .subscribe(() => {
          this.loadedSubsidySection = undefined;
          this.reset();
        });
    }
    else {
      this.reset();
    }
  }

  discardChanges(): void {
    this.internalSubsidySection = this.loadedSubsidySection ? {...this.loadedSubsidySection} : undefined;
    this.endEdit();
  }

  saveChanges(): void {
    const updated$ = this.loadedSubsidySection
      ? this.service.updateSubsidySection(this.projectId, this.internalSubsidySection)
      : this.service.addSubsidySection(this.projectId, this.internalSubsidySection);

    updated$.subscribe(data => {
      this.loadedSubsidySection = data;
      this.internalSubsidySection = data ? {...data} : undefined;
      this.endEdit();
    });
  }

  private reset(): void {
    this.subsidyName = '';
    this.internalSubsidySection = undefined;
  }

  private filterSubsidyName(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.subsidyNames.filter(option => option.toLowerCase().includes(filterValue));
  }
}
